import React from 'react';

import DicomStoreDirectoriesSelector from 'app/components/DicomStoreDirectoriesSelector';
import { ConfigureProtocolsInlineComponent } from 'app/components/ConfigureProtocolsButton';
import FlatPanelConfiguration from 'app/components/FlatPanelConfiguration';
import PMSConfiguration from 'app/components/PMSConfiguration';
import UserMappingConfiguration from 'app/components/UserMappingConfiguration';
import XRayGeneratorConfiguration from 'app/components/XRayGeneratorConfiguration';
import TelemetryConfiguration from 'app/components/XRayGeneratorConfiguration/TelemetryConfiguration';
import CommnunicationModeConfiguration from 'app/components/XRayGeneratorConfiguration/CommunicationModeConfiguration';
import XRayGeneratorAcquisitionConstantsConfiguration from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration';
import XRayGeneratorHistoryExtractor from 'app/components/XRayGeneratorHistoryExtractor';
import XRayGeneratorFaultsDisplay from 'app/components/XRayGeneratorFaultsDisplay';
import ProcessingFineTuningConfiguration from 'app/components/AdvancedConfiguration/ProcessingFineTuningConfiguration';
import PostProcessingConfiguration from 'app/components/PostProcessingConfiguration';
import NoXRayDeletionConfiguration from 'app/components/AdvancedConfiguration/NoXRayDeletionConfiguration';
import AIAnalysisDisabledConfiguration from 'app/components/AdvancedConfiguration/AIAnalysisDisabledConfiguration';
import ConstructorPanelConfiguration from 'app/containers/AccountPage/ConstructorPanelConfiguration';
import ClinicLogoForm from 'app/components/ClinicLogoForm';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';

import StorageDeletionSettings from './settings/StorageDeletionSettings';
import ClinicInfoSetting from './settings/ClinicInfoSetting';
import PACSAssociationConfiguration from './settings/PACSAssociationConfiguration';
import PACSWorklistConfiguration from './settings/PACSWorklistConfiguration';
import GeneratorSoftwareVersion from './settings/GeneratorSoftwareVersion';
import AiOnlyToggle from './settings/AiOnlyToggle';
import { PACSStatusSetting } from 'app/containers/SettingsPage/settings/PACSStatusSetting';
import ProtocolsConfiguration from 'app/containers/SettingsPage/settings/ProtocolsConfiguration';
import ImageRotationConfiguration from 'app/components/ImageRotationConfiguration';

/**
 * A setting rendered inside a SettingsRow (title on left, component on right).
 */
interface RowSettingDefinition {
  type: 'row';
  key: string;
  titleId: string;
  component: React.ComponentType;
  isVisible?: () => boolean;
}

/**
 * A setting that manages its own layout (no SettingsRow wrapper).
 * Used when a component renders multiple rows or has conditional visibility
 * driven by React context/hooks rather than static checks.
 */
interface ContentSettingDefinition {
  type: 'content';
  key: string;
  component: React.ComponentType;
  isVisible?: () => boolean;
}

export type SettingDefinition = RowSettingDefinition | ContentSettingDefinition;

export interface TabDefinition {
  key: string;
  titleId: string;
  isVisible: () => boolean;
  passwordProtected?: boolean;
  settings: SettingDefinition[];
}

export const TAB_DEFINITIONS: TabDefinition[] = [
  {
    key: 'report',
    titleId: 'settings.tab.report',
    isVisible: () => true,
    settings: [
      {
        type: 'row',
        key: 'clinic_info',
        titleId: 'account.report.info',
        component: ClinicInfoSetting,
      },
      {
        type: 'row',
        key: 'clinic_logo',
        titleId: 'clinicLogo.clinicLogo',
        component: ClinicLogoForm,
      },
    ],
  },
  {
    key: 'storage',
    titleId: 'settings.tab.storage',
    isVisible: () => process.env.PLATFORM === 'electron',
    settings: [
      {
        type: 'row',
        key: 'save_dir',
        titleId: 'account.configuration.acquisition_save_dir',
        component: DicomStoreDirectoriesSelector,
      },
      {
        type: 'row',
        key: 'storage_deletion',
        titleId: 'account.configuration.storage_deletion_enable',
        component: StorageDeletionSettings,
        isVisible: () => isAcquisitionSoftware(),
      },
    ],
  },
  {
    key: 'protocols',
    titleId: 'settings.tab.protocols',
    isVisible: () => true,
    settings: [
      {
        type: 'content',
        key: 'protocols',
        component: ProtocolsConfiguration,
      },
    ],
  },
  {
    key: 'flat_panel',
    titleId: 'settings.tab.flat_panel',
    isVisible: () => isAcquisitionSoftware(),
    settings: [
      {
        type: 'row',
        key: 'rotation',
        titleId: 'account.configuration.rotation.title',
        component: ImageRotationConfiguration,
      },
      {
        type: 'row',
        key: 'flat_panel',
        titleId: 'account.configuration.flat_panel.title',
        component: FlatPanelConfiguration,
      },
    ],
  },
  {
    key: 'pms',
    titleId: 'settings.tab.pms',
    isVisible: () => process.env.PLATFORM === 'electron',
    settings: [
      {
        type: 'content',
        key: 'pms',
        component: PMSConfiguration,
      },
    ],
  },
  {
    key: 'dicom',
    titleId: 'settings.tab.dicom',
    isVisible: () => true,
    settings: [
      {
        type: 'row',
        key: 'user_mapping',
        titleId: 'account.configuration.user_mapping',
        component: UserMappingConfiguration,
      },
      {
        type: 'row',
        key: 'pacsStatus',
        titleId: 'account.configuration.pacsStatus',
        component: PACSStatusSetting,
      },
      {
        type: 'row',
        key: 'pacs_association',
        titleId: 'pacs.configuration.title.pacs',
        component: PACSAssociationConfiguration,
      },
      {
        type: 'row',
        key: 'pacs_worklist',
        titleId: 'pacs.configuration.title.worklist',
        component: PACSWorklistConfiguration,
      },
    ],
  },
  {
    key: 'generator',
    titleId: 'settings.tab.generator',
    isVisible: () => process.env.PLATFORM === 'electron' && isAcquisitionSoftware(),
    settings: [
      {
        type: 'content',
        key: 'version',
        component: GeneratorSoftwareVersion,
      },
      {
        type: 'row',
        key: 'serial_port',
        titleId: 'account.configuration.xray_generator.serial_port',
        component: XRayGeneratorConfiguration,
      },
      {
        type: 'row',
        key: 'telemetry',
        titleId: 'account.configuration.xray_generator.telemetry',
        component: TelemetryConfiguration,
      },
      {
        type: 'row',
        key: 'communication_mode',
        titleId: 'account.configuration.xray_generator.communication_mode',
        component: CommnunicationModeConfiguration,
      },
      {
        type: 'row',
        key: 'acquisition_constants',
        titleId: 'account.configuration.xray_generator.acquisition_constants',
        component: XRayGeneratorAcquisitionConstantsConfiguration,
      },
      {
        type: 'row',
        key: 'extract_history',
        titleId: 'account.configuration.xray_generator.extract_history',
        component: XRayGeneratorHistoryExtractor,
      },
      {
        type: 'row',
        key: 'fault_display',
        titleId: 'account.configuration.xray_generator.fault_history.display',
        component: XRayGeneratorFaultsDisplay,
      },
    ],
  },
  {
    key: 'vizion',
    titleId: 'settings.tab.vizion',
    isVisible: () => true,
    settings: [
      {
        type: 'row',
        key: 'processing_finetuning',
        titleId: 'account.configuration.processing_finetuning_enabled',
        component: ProcessingFineTuningConfiguration,
      },
      {
        type: 'row',
        key: 'processing',
        titleId: 'account.configuration.processing',
        component: PostProcessingConfiguration,
      },
    ],
  },
  {
    key: 'advanced',
    titleId: 'settings.tab.advanced',
    isVisible: () => true,
    passwordProtected: true,
    settings: [
      {
        type: 'row',
        key: 'no_xray_deletion',
        titleId: 'account.configuration.no_xray_deletion',
        component: NoXRayDeletionConfiguration,
      },
      {
        type: 'row',
        key: 'ai_analysis_disabled',
        titleId: 'account.configuration.ai_analysis_disabled',
        component: AIAnalysisDisabledConfiguration,
      },
      {
        type: 'row',
        key: 'ai_only',
        titleId: 'account.configuration.ai_only.title',
        component: AiOnlyToggle,
        isVisible: () => process.env.PLATFORM === 'electron',
      },
      {
        type: 'row',
        key: 'constructor',
        titleId: 'account.configuration.constructor_options',
        component: ConstructorPanelConfiguration,
      },
    ],
  },
];
